import { IntlShape } from "react-intl";

export const INVOICE_ATTACHMENT_ACCEPT =
  "application/pdf, text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png";

export type InvoiceFormStop = {
  id: number;
  sequence: number;
  type: string;
  display_name: string | null;
  full_address?: string;
};

export type BucketAttachmentRow = {
  file: {
    id: number;
    name: string;
    reference_number: string;
    url: string;
  };
  stop: InvoiceFormStop | null;
  attachment_bucket?: { id: number };
};

export type GroupedBucketAttachment = {
  file: BucketAttachmentRow["file"];
  entireShipment: boolean;
  stops: InvoiceFormStop[];
};

export type DocumentForSelection = {
  entireShipment: boolean;
  stopIds: number[];
};

export const isDocumentForSelectionValid = ({ entireShipment, stopIds }: DocumentForSelection) =>
  entireShipment || stopIds.length > 0;

export const groupBucketAttachmentsByFile = (
  attachments: BucketAttachmentRow[],
  bucketId: number,
): GroupedBucketAttachment[] => {
  const grouped = new Map<number, GroupedBucketAttachment>();

  (attachments || [])
    .filter((row) => row?.attachment_bucket?.id === bucketId && row?.file?.id)
    .forEach((row) => {
      const fileId = row.file.id;
      if (!grouped.has(fileId)) {
        grouped.set(fileId, {
          file: row.file,
          entireShipment: row.stop === null,
          stops: [],
        });
      }

      const entry = grouped.get(fileId)!;
      if (row.stop === null) {
        entry.entireShipment = true;
        entry.stops = [];
        return;
      }

      if (!entry.stops.some((stop) => stop.id === row.stop!.id)) {
        entry.stops.push(row.stop);
      }
      entry.entireShipment = false;
    });

  return Array.from(grouped.values()).map((entry) => ({
    ...entry,
    stops: [...entry.stops].sort((left, right) => left.sequence - right.sequence),
  }));
};

export const formatStopSequenceNumbers = (sequences: number[], intl: IntlShape): string => {
  if (!sequences.length) return "";
  if (sequences.length === 1) return String(sequences[0]);
  const and = intl.formatMessage({ id: "general.and", defaultMessage: "and" });
  if (sequences.length === 2) return `${sequences[0]} ${and} ${sequences[1]}`;
  return `${sequences.slice(0, -1).join(", ")} ${and} ${sequences[sequences.length - 1]}`;
};

export const buildStopLabel = (sequences: number | number[], intl: IntlShape): string => {
  const list = Array.isArray(sequences) ? sequences : [sequences];
  return intl.formatMessage(
    {
      id: "billing.invoiceAttachment.stopLabel",
      defaultMessage: "Stop {sequences}",
    },
    { sequences: formatStopSequenceNumbers(list, intl) },
  );
};

export const buildGroupedAttachmentLabel = (
  attachment: Pick<GroupedBucketAttachment, "entireShipment" | "stops">,
  intl: IntlShape,
): string => {
  if (attachment.entireShipment) {
    return intl.formatMessage({
      id: "billing.invoiceAttachment.entireShipment",
      defaultMessage: "Entire shipment",
    });
  }

  return buildStopLabel(
    attachment.stops.map((stop) => stop.sequence),
    intl,
  );
};

export const buildDocumentForSelectionFromGroup = (
  attachment: GroupedBucketAttachment,
): DocumentForSelection => ({
  entireShipment: attachment.entireShipment,
  stopIds: attachment.stops.map((stop) => stop.id),
});

export const toggleStopSelection = (
  selection: DocumentForSelection,
  stopId: number,
  checked: boolean,
): DocumentForSelection => {
  if (checked) {
    return {
      entireShipment: false,
      stopIds: [...new Set([...selection.stopIds, stopId])],
    };
  }

  return {
    entireShipment: false,
    stopIds: selection.stopIds.filter((id) => id !== stopId),
  };
};

export const selectEntireShipment = (): DocumentForSelection => ({
  entireShipment: true,
  stopIds: [],
});

export const formatStopTypeLabel = (type?: string) => {
  if (!type) return "";
  return `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
};

export const DELIVERY_STOP_TYPE = "dropoff";

export const isDeliveryStop = (stop: Pick<InvoiceFormStop, "type">) =>
  stop.type?.toLowerCase() === DELIVERY_STOP_TYPE;

/** Delivery stops only; keeps each stop's original sequence from the full route. */
export const filterDeliveryStops = (stops: InvoiceFormStop[]): InvoiceFormStop[] =>
  (stops || []).filter(isDeliveryStop);

export const truncateAttachmentFileName = (name?: string | null, maxLength = 30) => {
  if (!name) return "";
  if (name.length <= maxLength) return name;
  return `${name.slice(0, maxLength)}…`;
};
